import { useState } from "react";
import { X, RefreshCw, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

/**
 * Reusable SMS quick-send modal.
 *
 * Props:
 *   to: {
 *     to_phone: string (required),
 *     customer_name?: string,
 *     customer_id?: string,
 *     booking_id?: string,
 *     trial_lead_id?: string,
 *     appointment_id?: string,
 *   }
 *   template_key?: string   // default "manual_inbound_reply"
 *   title?: string          // default "Reply via SMS"
 *   onClose: () => void
 *   onSent: () => void
 */
export function SmsReplyModal({ to, template_key = "manual_inbound_reply", title = "Reply via SMS", onClose, onSent }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (sending) return;                                  // Section 19 — prevent duplicate sends from repeated taps
    if (!body.trim()) { toast.error("Type a message before sending"); return; }
    if (!to?.to_phone) { toast.error("Recipient phone is missing"); return; }
    setSending(true);
    try {
      const payload = {
        to_phone: to.to_phone,
        body: body.trim(),
        recipient_name: to.customer_name || "",
        customer_id: to.customer_id || "",
        booking_id: to.booking_id || "",
        trial_lead_id: to.trial_lead_id || "",
        appointment_id: to.appointment_id || "",
        template_key,
      };
      const res = await axios.post(`${API}/admin/follow-up/sms/send`, payload, { headers: auth() });
      if (res.data?.ok) {
        toast.success("SMS sent");
        onSent?.();
      } else {
        toast.error(res.data?.error || "Send failed — see Communications log");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  const linked = to.booking_id || to.trial_lead_id || to.appointment_id || to.customer_id || "";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-lg sm:w-auto rounded-t-2xl sm:rounded-none max-h-[92vh] sm:max-h-none flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="sms-reply-modal"
      >
        <div className="sticky top-0 z-10 flex justify-between items-start px-4 sm:px-5 pt-4 sm:pt-5 pb-3 bg-white border-b border-[#282828]/10">
          <div>
            <h3 className="text-[16px] font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{title}</h3>
            <p className="text-[11px] text-[#676664]/70 mt-0.5">
              To: <span className="font-mono">{to.to_phone}</span>
              {to.customer_name ? ` · ${to.customer_name}` : ""}
            </p>
            {linked && (
              <p className="text-[10px] text-[#676664]/50 mt-0.5">Linked: <span className="font-mono">{linked}</span></p>
            )}
          </div>
          <button onClick={onClose} className="text-[#676664] p-1 -m-1" data-testid="sms-reply-close" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">
          <textarea rows={5} value={body} onChange={e => setBody(e.target.value)} placeholder="Type your message…"
            className="w-full border border-[#282828]/12 px-3 py-2 text-[13px] font-mono resize-y mb-2" data-testid="sms-reply-body" />
          <p className="text-[10px] text-[#676664]/50">{body.length}/160 chars — longer messages send as multi-part SMS.</p>
        </div>
        <div
          className="sticky bottom-0 z-10 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 sm:px-5 py-3 bg-white border-t border-[#282828]/10"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <button onClick={onClose} className="w-full sm:w-auto px-4 py-3 text-[11px] uppercase tracking-wider border border-[#282828]/12 text-[#676664]">Cancel</button>
          <button onClick={send} disabled={sending || !body.trim()} data-testid="sms-reply-send"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-3 text-[11px] uppercase tracking-wider bg-[#9A7B4F] text-white hover:bg-[#8B6B3F] disabled:opacity-50">
            {sending ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {sending ? "Sending…" : "Send SMS"}
          </button>
        </div>
      </div>
    </div>
  );
}
